/**
 * Default appointment-dialog UI.
 * Pure render component — receives all state/handlers from useAppointmentDialog.
 */

import { Trash2, Users, Palette, FileText, CalendarPlus, Stethoscope, CalendarDays, ExternalLink, Check, CheckCheck, Search, X, Eye, EyeOff } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { ColorPicker } from '@/components/shared/ColorPicker'
import { DEFAULT_EVENT_COLOR } from '@/lib/constants'
import { MAX_PP_PER_DAY } from '@/config'
import type { AppointmentDialogState } from '../useAppointmentDialog'
import { GhostDayColumn } from '../GhostDayColumn'
import type { AppointmentType } from '@/types/calendar'

const GHOST_CALENDAR_TYPE: Partial<Record<AppointmentType, AppointmentType>> = {
	ZUB: 'DH',
	DH: 'ZUB',
}

export function AppointmentDialogDefault(state: AppointmentDialogState) {
	const {
		open,
		onClose,
		mode,
		viewMode,
		register,
		errors,
		setValue,
		submitForm,
		background,
		isLoading,
		isLoadingDetails,
		isDeleting,
		isSearching,
		isEventToday,
		isNoShowBackground,
		isCancelledBg,
		isPPDisabled,
		actionTypes,
		selectedPatients,
		patientData,
		filteredPatients,
		patientsListRef,
		showPatientsFade,
		checkPatientsFade,
		handleAddPatient,
		handleRemovePatient,
		handlePatientDataChange,
		patientQuery,
		setPatientQuery,
		patientDropdownOpen,
		setPatientDropdownOpen,
		patientSelectedIndex,
		setPatientSelectedIndex,
		handlePatientKeyDown,
		patientsToDailyNotes,
		dailyNotesStatus,
		handleToggleDailyNotes,
		noShowUpPaid,
		setNoShowUpPaid,
		sendCancelSms,
		setSendCancelSms,
		cancelType,
		setCancelType,
		cancelSmsData,
		handleDelete,
		calendarType,
		showGhostColumn,
		toggleGhostColumn,
		selectedDate,
	} = state

	const ghostCalendarType = GHOST_CALENDAR_TYPE[calendarType]

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className={`${showGhostColumn && ghostCalendarType ? 'max-w-6xl' : 'max-w-4xl'} max-h-[90vh] flex flex-col overflow-hidden`}>
				{/* pr-10 keeps the ghost-column toggle clear of the absolute close button */}
				<DialogHeader className="pb-2 pr-10 flex-shrink-0">
					<DialogTitle className="flex items-center gap-3">
						<span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#f5ebe0]">
							<CalendarPlus className="w-4 h-4 text-[#d7ad6c]" />
						</span>
						<span style={{ fontSize: 'var(--font-size-dialog-title)' }}>{mode === 'create' ? 'Nový termín' : 'Upravit termín'}</span>
						{ghostCalendarType && (
							<button
								type="button"
								onClick={toggleGhostColumn}
								title={showGhostColumn ? `Skrýt termíny ${ghostCalendarType}` : `Zobrazit termíny ${ghostCalendarType}`}
								className={`ml-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
									showGhostColumn
										? 'bg-[#f5ebe0] border-[#d7ad6c] text-[#d7ad6c]'
										: 'bg-white border-[#e8e4df] text-[#8c8275] hover:bg-[#faf8f5] hover:border-[#d7ad6c] hover:text-[#d7ad6c]'
								}`}
							>
								{showGhostColumn ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
								{ghostCalendarType}
							</button>
						)}
					</DialogTitle>
				</DialogHeader>

				<div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
					<div className="flex-1 overflow-y-auto min-w-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}>
				{/* Loading state when fetching appointment details */}
				{mode === 'edit' && isLoadingDetails && (
					<div className="flex items-center justify-center py-12">
						<div className="flex items-center gap-3 text-[#8c8275]">
							<div className="w-5 h-5 border-2 border-[#d7ad6c] border-t-transparent rounded-full animate-spin" />
							<span>Načítání termínu...</span>
						</div>
					</div>
				)}

				<form onSubmit={submitForm} className={`space-y-4 ${mode === 'edit' && isLoadingDetails ? 'hidden' : ''}`}>
					{/* Title - compact, max 2 lines */}
					<div>
						<Textarea
							id="title"
							placeholder="Název termínu..."
							{...register('title')}
							rows={2}
							className={`text-lg font-semibold text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors resize-none leading-tight ${errors.title ? 'border-red-400' : ''}`}
							style={{ minHeight: '52px', maxHeight: '52px' }}
						/>
						{errors.title && (
							<p
								className="text-red-500 mt-1"
								style={{ fontSize: 'var(--font-size-dialog-label)' }}
							>
								{errors.title.message}
							</p>
						)}
						{/* No-show penalty paid checkbox */}
						{isNoShowBackground && (
							<button
								type="button"
								onClick={() => setNoShowUpPaid(!noShowUpPaid)}
								className="flex items-center gap-3 mt-2 p-2.5 rounded-xl bg-red-50 border border-red-200 w-full text-left hover:bg-red-100 transition-colors cursor-pointer"
							>
								<div
									className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
										noShowUpPaid
											? 'bg-red-500 border-red-500'
											: 'bg-white border-red-400'
									}`}
								>
									{noShowUpPaid && (
										<span className="text-white text-xs font-bold">✓</span>
									)}
								</div>
								<span
								className="text-red-700"
								style={{ fontSize: 'var(--font-size-dialog-label)' }}
							>
								Sankce uhrazena
							</span>
							</button>
						)}
					</div>

					{/* Patient search - half width */}
					<div>
						<Label
							className="flex items-center gap-2 text-[#5c5142] font-medium mb-2"
							style={{ fontSize: 'var(--font-size-dialog-label)' }}
						>
							<Users className="w-4 h-4 text-[#8c8275]" />
							Pacienti
						</Label>
						<div className="relative w-1/2">
							<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8c8275]" />
							<Input
								type="text"
								placeholder="Vyhledat pacienta..."
								value={patientQuery}
								onChange={(e) => {
									setPatientQuery(e.target.value)
									setPatientDropdownOpen(e.target.value.length >= 2)
									setPatientSelectedIndex(0)
								}}
								onFocus={() => patientQuery.length >= 2 && setPatientDropdownOpen(true)}
								onKeyDown={handlePatientKeyDown}
								className="pl-10 pr-10 text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors"
								style={{
									height: 'var(--input-height-dialog)',
									fontSize: 'var(--font-size-dialog-input)',
								}}
							/>
							{patientQuery && (
								<button
									type="button"
									onClick={() => {
										setPatientQuery('')
										setPatientDropdownOpen(false)
									}}
									className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#8c8275] hover:text-[#5c5142] hover:bg-[#f5ebe0] transition-colors"
								>
									<X className="h-3.5 w-3.5" />
								</button>
							)}

							{/* Dropdown */}
							{patientDropdownOpen && patientQuery.length >= 2 && (
								<div className="absolute z-50 mt-1 w-full rounded-xl border border-[#e8e4df] bg-white shadow-lg overflow-hidden">
									{isSearching ? (
										<div className="flex items-center justify-center p-3">
											<div className="h-4 w-4 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
											<span
												className="ml-2 text-[#8c8275]"
												style={{ fontSize: 'var(--font-size-dialog-input)' }}
											>
												Vyhledávání...
											</span>
										</div>
									) : filteredPatients.length === 0 ? (
										<div
											className="p-3 text-center text-[#8c8275]"
											style={{ fontSize: 'var(--font-size-dialog-input)' }}
										>
											Žádný pacient nenalezen
										</div>
									) : (
										<ul className="max-h-40 overflow-y-auto py-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}>
											{filteredPatients.map((patient, index) => (
												<li key={patient.id}>
													<button
														type="button"
														onClick={() => handleAddPatient(patient)}
														className={`w-full px-4 py-2.5 text-left transition-colors ${
															index === patientSelectedIndex
																? 'bg-[#f5ebe0] text-[#3d3428]'
																: 'hover:bg-[#faf8f5] text-[#3d3428]'
														}`}
														style={{ fontSize: 'var(--font-size-dialog-input)' }}
													>
														<span className="font-medium">
															{patient.lastName} {patient.firstName}
														</span>
														<span className="ml-2 text-[#8c8275]">
															- {patient.personalId}
														</span>
													</button>
												</li>
											))}
										</ul>
									)}
								</div>
							)}
						</div>
					</div>

					{/* Patient list with per-patient action type and pricing */}
					{selectedPatients.length > 0 && (
						<div className="relative">
						<div ref={patientsListRef} onScroll={checkPatientsFade} className="space-y-2 max-h-[184px] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}>
							{selectedPatients.map((patient) => {
								const pData = patientData[patient.id] || { actionType: '', priceFrom: '', priceTo: '' }
								const isCheckedForDailyNotes = patientsToDailyNotes.has(patient.personalId)
								const dailyNoteStatus = dailyNotesStatus?.[patient.personalId]

								return (
									<div key={patient.id} className="flex items-center gap-3">
										{/* Patient card - 1/3 width */}
										<div className="w-1/2 flex-shrink-0 flex items-center justify-between rounded-xl bg-gradient-to-r from-[#fdf8f0] to-[#f9f1e4] border border-[#e5c896] px-3 py-2.5 shadow-sm">
											<div className="flex items-center gap-2 min-w-0">
												{isEventToday && (
													<input
														type="checkbox"
														checked={isCheckedForDailyNotes}
														onChange={() => handleToggleDailyNotes(patient.personalId)}
														title={
															isCheckedForDailyNotes
																? 'Pacient přišel – započítán do týdenního reportu. Odškrtnutím a uložením docházku zrušíte (záznam v Denním zápise zůstane).'
																: 'Zaškrtnutím potvrdíte, že pacient přišel: vytvoří se záznam v Denním zápise a pacient se započítá do týdenního reportu.'
														}
														className="w-3.5 h-3.5 rounded border-2 border-[#d7ad6c] bg-white checked:bg-[#c49b5a] checked:border-[#c49b5a] focus:ring-[#d7ad6c] focus:ring-offset-0 cursor-pointer appearance-none relative checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center checked:after:text-white checked:after:text-[10px] checked:after:font-bold flex-shrink-0"
													/>
												)}
												<span
													className="text-[#3d3428] truncate"
													style={{ fontSize: 'var(--font-size-dialog-input)' }}
												>
													<span className="font-semibold">{patient.lastName} {patient.firstName}</span>
													<span className="text-[#8c8275] font-normal"> - {patient.personalId}</span>
													{isEventToday && dailyNoteStatus?.contains && (
														<span className="ml-1 text-[10px] text-[#d7ad6c]">(DZ)</span>
													)}
												</span>
											</div>
											<div className="flex items-center gap-0.5 flex-shrink-0">
												<a
													href={`/patient/${patient.id}`}
													target="_blank"
													rel="noopener noreferrer"
													onClick={(e) => e.stopPropagation()}
													title="Otevřít profil pacienta"
													className="p-1 rounded-lg text-[#8c8275] hover:text-[#d7ad6c] hover:bg-[#f5ebe0] transition-colors"
												>
													<ExternalLink className="h-3.5 w-3.5" />
												</a>
												<button
													type="button"
													onClick={() => handleRemovePatient(patient.id)}
													className="p-1 rounded-lg text-[#8c8275] hover:text-red-500 hover:bg-red-50 transition-colors"
												>
													<Trash2 className="h-3.5 w-3.5" />
												</button>
											</div>
										</div>

										{/* Per-patient inputs - 2/3 width */}
										<div className="flex-1 grid grid-cols-3 gap-2">
											{/* Action type select */}
											{actionTypes.length > 0 ? (
												<Select
													value={pData.actionType || ''}
													onValueChange={(value) => handlePatientDataChange(patient.id, 'actionType', value)}
												>
													<SelectTrigger
														className="font-semibold text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none"
														style={{
															height: 'var(--input-height-dialog)',
															fontSize: 'var(--font-size-dialog-input)',
														}}
													>
														<SelectValue placeholder="Typ" />
													</SelectTrigger>
													<SelectContent className="bg-white border border-[#e8e4df] rounded-xl shadow-lg">
														{actionTypes.map((type) => {
															const isDisabled = type.value === 'pp' && isPPDisabled
															return (
																<SelectItem
																	key={type.value}
																	value={type.value}
																	disabled={isDisabled}
																	className={`text-[#3d3428] focus:bg-[#f5ebe0] focus:text-[#3d3428] ${isDisabled ? 'opacity-50' : ''}`}
																>
																	{type.label}
																	{isDisabled && (
																		<span className="ml-1 text-xs text-[#8c8275]">(max {MAX_PP_PER_DAY})</span>
																	)}
																</SelectItem>
															)
														})}
													</SelectContent>
												</Select>
											) : (
												<div />
											)}

											{/* Price from */}
											<Input
												type="number"
												placeholder="Cena od"
												value={pData.priceFrom}
												onChange={(e) => handlePatientDataChange(patient.id, 'priceFrom', e.target.value)}
												className="font-semibold text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors"
												style={{
													height: 'var(--input-height-dialog)',
													fontSize: 'var(--font-size-dialog-input)',
												}}
											/>

											{/* Price to */}
											<Input
												type="number"
												placeholder="Cena do"
												value={pData.priceTo}
												onChange={(e) => handlePatientDataChange(patient.id, 'priceTo', e.target.value)}
												className="font-semibold text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors"
												style={{
													height: 'var(--input-height-dialog)',
													fontSize: 'var(--font-size-dialog-input)',
												}}
											/>
										</div>
									</div>
								)
							})}
						</div>
						{showPatientsFade && (
							<div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none rounded-b-xl transition-opacity" />
						)}
						</div>
					)}

					{/* Date + Time row */}
					<div className="flex items-end gap-4">
						{/* Date */}
						<div className="space-y-2">
							<Label
								className="flex items-center gap-2 text-[#5c5142] font-medium"
								style={{ fontSize: 'var(--font-size-dialog-label)' }}
							>
								<CalendarDays className="w-3.5 h-3.5 text-[#8c8275]" />
								Datum
							</Label>
							<Input
								type="date"
								{...register('date')}
								className={`w-44 text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors ${errors.date ? 'border-red-400' : ''}`}
								style={{
									height: 'var(--input-height-dialog)',
									fontSize: 'calc(var(--font-size-dialog-input) + 2px)',
								}}
							/>
						</div>

						{/* Time from */}
						<div className="space-y-2">
							<Label
								className="text-[#5c5142] font-medium"
								style={{ fontSize: 'var(--font-size-dialog-label)' }}
							>
								Od
							</Label>
							<Input
								type="time"
								{...register('startTime')}
								className={`w-32 text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors ${errors.startTime ? 'border-red-400' : ''}`}
								style={{
									height: 'var(--input-height-dialog)',
									fontSize: 'calc(var(--font-size-dialog-input) + 2px)',
								}}
							/>
						</div>

						{/* Time to */}
						<div className="space-y-2">
							<Label
								className="text-[#5c5142] font-medium"
								style={{ fontSize: 'var(--font-size-dialog-label)' }}
							>
								Do
							</Label>
							<Input
								type="time"
								{...register('endTime')}
								className={`w-32 text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors ${errors.endTime ? 'border-red-400' : ''}`}
								style={{
									height: 'var(--input-height-dialog)',
									fontSize: 'calc(var(--font-size-dialog-input) + 2px)',
								}}
							/>
						</div>
					</div>

					{/* Notes - Two columns */}
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label
								htmlFor="note"
								className="flex items-center gap-2 text-[#5c5142] font-medium"
								style={{ fontSize: 'var(--font-size-dialog-label)' }}
							>
								<FileText className="w-3.5 h-3.5 text-[#8c8275]" />
								Poznámka
							</Label>
							<Textarea
								id="note"
								placeholder="Poznámka k termínu..."
								{...register('note')}
								rows={2}
								className="text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors resize-none"
								style={{ fontSize: 'var(--font-size-dialog-input)' }}
							/>
						</div>
						<div className="space-y-2">
							<Label
								htmlFor="doctorsNote"
								className="flex items-center gap-2 text-[#5c5142] font-medium"
								style={{ fontSize: 'var(--font-size-dialog-label)' }}
							>
								<Stethoscope className="w-3.5 h-3.5 text-[#8c8275]" />
								Poznámka ordinace
							</Label>
							<Textarea
								id="doctorsNote"
								placeholder="Poznámka ordinace..."
								{...register('doctorsNote')}
								rows={2}
								readOnly={viewMode === 'reception'}
								className={`text-[#3d3428] bg-[#faf8f5] border border-[#e8e4df] rounded-xl focus:border-[#d7ad6c] focus:outline-none transition-colors resize-none ${viewMode === 'reception' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
								style={{ fontSize: 'var(--font-size-dialog-input)' }}
							/>
						</div>
					</div>

					{/* Cancel SMS section */}
					{isCancelledBg && (
						<>
							{mode === 'edit' && cancelSmsData && (
								<div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 w-fit">
									<div className="flex items-center gap-2">
										{cancelSmsData.status === 'DELIVERED' ? (
											<CheckCheck className="w-4 h-4 text-[#d7ad6c]" />
										) : cancelSmsData.status === 'SENT' ? (
											<Check className="w-4 h-4 text-[#d7ad6c]" />
										) : cancelSmsData.status === 'PENDING' ? (
											<Check className="w-4 h-4 text-[#e8e4df]" />
										) : null}
									</div>
									<span
										className="text-[#3d3428]"
										style={{ fontSize: 'var(--font-size-dialog-input)' }}
									>
										Zrušeno na základě:{' '}
										<span className="font-medium">
											{cancelSmsData.cancelType === 'call' && 'hovoru'}
											{cancelSmsData.cancelType === 'sms' && 'SMS zprávy'}
											{cancelSmsData.cancelType === 'person' && 'osobní návštěvy'}
											{cancelSmsData.cancelType === 'email' && 'emailu'}
										</span>
									</span>
								</div>
							)}

							{!cancelSmsData && (
								<div className="flex items-center gap-4">
									<button
										type="button"
										onClick={() => {
											setSendCancelSms(!sendCancelSms)
											if (sendCancelSms) setCancelType('')
										}}
										className="flex items-center gap-2 hover:bg-[#f5ebe0] rounded-lg px-2 py-1 transition-colors cursor-pointer flex-shrink-0"
									>
										<div
											className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
												sendCancelSms
													? 'bg-[#d7ad6c] border-[#d7ad6c]'
													: 'bg-white border-[#d7ad6c]'
											}`}
										>
											{sendCancelSms && (
												<span className="text-white text-xs font-bold">✓</span>
											)}
										</div>
										<span
											className="text-[#3d3428] whitespace-nowrap"
											style={{ fontSize: 'var(--font-size-dialog-input)' }}
										>
											Odeslat SMS o zrušení
										</span>
									</button>

									{sendCancelSms && (
										<div className="flex items-center gap-3 flex-shrink-0">
											<span
												className="text-[#5c5142] whitespace-nowrap"
												style={{ fontSize: 'var(--font-size-dialog-input)' }}
											>
												na základě:
											</span>
											<div className="flex rounded-xl overflow-hidden border border-gray-300">
												{([
													{ value: 'call', label: 'Hovoru' },
													{ value: 'sms', label: 'SMS' },
													{ value: 'person', label: 'Osobně' },
													{ value: 'email', label: 'Emailu' },
												] as const).map((option) => (
													<button
														key={option.value}
														type="button"
														onClick={() => setCancelType(option.value)}
														className={`px-3 py-1.5 transition-colors cursor-pointer ${
															cancelType === option.value
																? 'bg-[#d7ad6c] text-white'
																: 'bg-white text-[#3d3428] hover:bg-[#f5ebe0]'
														}`}
														style={{ fontSize: 'var(--font-size-dialog-input)' }}
													>
														{option.label}
													</button>
												))}
											</div>
										</div>
									)}
								</div>
							)}
						</>
					)}

					{/* Actions */}
					<div className="flex items-center justify-between gap-3 pt-3 border-t border-[#e8e4df]">
						<div>
							{mode === 'edit' && (
								<Button
									type="button"
									variant="outline"
									onClick={handleDelete}
									disabled={isLoading || isDeleting}
									className="h-10 px-5 rounded-xl bg-red-500/10 border-red-300 text-red-600 hover:bg-red-500/20 hover:border-red-400 transition-all"
								>
									<Trash2 className="mr-2 h-4 w-4" />
									Smazat
								</Button>
							)}
						</div>
						<div className="flex gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={onClose}
								className="h-10 px-6 rounded-xl bg-white border border-[#e8e4df] text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-all"
							>
								Zrušit
							</Button>
							<Button
								type="submit"
								disabled={isLoading}
								className="h-10 px-6 rounded-xl bg-[#d7ad6c] text-white hover:bg-[#c49b5a] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
							>
								{isLoading
									? 'Ukládání...'
									: mode === 'create'
										? 'Vytvořit termín'
										: 'Uložit změny'}
							</Button>
						</div>
					</div>

					{/* Color picker - below buttons */}
					<div className="space-y-2">
						<Label
							className="flex items-center gap-2 text-[#5c5142] font-medium"
							style={{ fontSize: 'var(--font-size-dialog-label)' }}
						>
							<Palette className="w-3.5 h-3.5 text-[#8c8275]" />
							Barva
						</Label>
						<ColorPicker
							value={background || DEFAULT_EVENT_COLOR}
							onChange={(color) => setValue('background', color)}
						/>
					</div>
				</form>
					</div>

					{showGhostColumn && ghostCalendarType && (
						<GhostDayColumn calendarType={ghostCalendarType} date={selectedDate} />
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
